import { Component } from 'react';

import { Button } from '@iwoca/orion';
import { RootState } from '@iwProvider/configureStore';
import _ from 'lodash';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';

import styles from './IdCheckVerificationModal.module.css';
import PeopleToVerify from './peopleToVerify/PeopleToVerify';

const mapStateToProps = (state: RootState) => state.idCheckVerificationModal;

type PropsFromRedux = ReturnType<typeof mapStateToProps>;

class IdCheckVerificationModal extends Component<
    { onClose?: () => void } & PropsFromRedux
> {
    render() {
        const hasMultiplePeopleToVerify = this.props.peopleToVerify.length > 1;
        const id_verification_provider_name =
            this.props.id_verification_provider_name;

        return (
            <div className={styles.IdCheckVerificationModal}>
                <div className={styles.title}>
                    <FormattedMessage
                        id="_.modal.id_check_verification.title"
                        defaultMessage="Identity check"
                    />
                </div>
                <div className={styles.subTitle}>
                    {!hasMultiplePeopleToVerify ? (
                        <FormattedMessage
                            id="_.modal.id_check_verification.subTitle"
                            defaultMessage="We need to check your identity to make sure we're sending money to the right person. We use {id_verification_provider_name} verification, which is a secure way to do this. You'll just need your passport or driving license, and your smartphone."
                            values={{ id_verification_provider_name }}
                        />
                    ) : (
                        <FormattedMessage
                            id="_.modal.id_check_verification.subTitleMultiple"
                            defaultMessage="We need to verify your identity, as well as the identities of all guarantors, to ensure the funds are sent securely to the correct person. {id_verification_provider_name} is the most secure way to do this."
                            values={{ id_verification_provider_name }}
                        />
                    )}
                </div>
                <div className={styles.requirementsSubTitle}>
                    {!hasMultiplePeopleToVerify ? (
                        <FormattedMessage
                            id="_.modal.id_check_verification.requirementsSubTitle"
                            defaultMessage="Select your mobile number and click to send yourself a text below. Once you receive the text, click on the link to complete your check."
                        />
                    ) : (
                        <FormattedMessage
                            id="_.modal.id_check_verification.requirementsSubTitleMultiple"
                            defaultMessage="Select your mobile number below and click to send yourself a text, which will contain a link for you to complete the check. Do the same for everyone listed so they can complete theirs too (and maybe give them a heads up to speed things along!)."
                        />
                    )}
                </div>

                <PeopleToVerify />

                <div
                    className={styles.infoMessage}
                    data-testid="onfido-status-info"
                >
                    {this.renderInfoMessage()}
                </div>

                <div>
                    <Button
                        className={styles.proceedButton}
                        onClick={this.props.onClose}
                    >
                        <FormattedMessage
                            tagName="strong"
                            id="_.modal.id_check_verification.continue"
                            defaultMessage="Continue"
                        />
                    </Button>
                </div>
            </div>
        );
    }

    renderInfoMessage() {
        const { peopleToVerify, reachedMaxIdCheckAttempts } = this.props;

        const hasMultiplePeopleToVerify = peopleToVerify.length > 1;

        let message;

        _.map(peopleToVerify, (person) => {
            const hasSMSFailed = person.status === 'SMS failed';

            if (hasSMSFailed) {
                return (message = (
                    <FormattedMessage
                        id="_.modal.id_check_verification.sms_failed"
                        defaultMessage="Your SMS didn't go through. Please try it again by clicking 'Send SMS'."
                    />
                ));
            }

            if (reachedMaxIdCheckAttempts) {
                return (message = (
                    <FormattedMessage
                        id="_.modal.id_check_verification.reached_max_onfido_checks"
                        defaultMessage="Please give your account manager a call on {phoneNumber} to continue."
                        values={{
                            phoneNumber: (
                                <FormattedMessage
                                    tagName="strong"
                                    id="_.modal.id_check_verification.phone_number"
                                    defaultMessage="020 3778 0105"
                                />
                            )
                        }}
                    />
                ));
            }

            if (
                person.idCheckStatus === 'Something went wrong' &&
                !hasSMSFailed
            ) {
                message = (
                    <FormattedMessage
                        id="_.modal.id_check_verification.something_went_wrong_info"
                        defaultMessage="Something's gone wrong! Please try again and make sure everyone listed has completed the check."
                    />
                );
            }

            if (
                person.idCheckStatus === 'Not started yet' &&
                !hasMultiplePeopleToVerify
            ) {
                message = (
                    <FormattedMessage
                        id="_.modal.id_check_verification.make_sure_all_texts_sent_info"
                        defaultMessage="Please give us a call on {phoneNumber} if you need help completing this step"
                        values={{
                            phoneNumber: (
                                <span>
                                    <FormattedMessage
                                        tagName="strong"
                                        id="_.modal.id_check_verification.phone_number"
                                        defaultMessage="020 3778 0105"
                                    />
                                </span>
                            )
                        }}
                    />
                );
            }

            if (
                person.idCheckStatus === 'Not started yet' &&
                hasMultiplePeopleToVerify
            ) {
                message = (
                    <FormattedMessage
                        id="_.modal.id_check_verification.make_sure_all_texts_sent_info_multiple_guarantors"
                        defaultMessage="Please ensure that everyone listed completed the check. Give us a call on {phoneNumber} if you need help completing this step."
                        values={{
                            phoneNumber: (
                                <FormattedMessage
                                    tagName="strong"
                                    id="_.modal.id_check_verification.phone_number"
                                    defaultMessage="020 3778 0105"
                                />
                            )
                        }}
                    />
                );
            }

            if (_.includes(person.idCheckStatus, 'Verifying')) {
                message = (
                    <FormattedMessage
                        id="_.modal.id_check_verification.make_sure_all_texts_sent_info_multiple_guarantors"
                        defaultMessage="Please ensure that everyone listed completed the check. Give us a call on {phoneNumber} if you need help completing this step."
                        values={{
                            phoneNumber: (
                                <FormattedMessage
                                    tagName="strong"
                                    id="_.modal.id_check_verification.phone_number"
                                    defaultMessage="020 3778 0105"
                                />
                            )
                        }}
                    />
                );
            }
        });
        return message;
    }
}

// eslint-disable-next-line import/no-default-export
export default connect(mapStateToProps)(IdCheckVerificationModal);
